import React from 'react';
import Link from '@v2/shared/components/Link';
import { useCart } from '@v2/shared/lib/cart';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * The cart, as an icon with a count on it — the `cart-info` block of MagSpot's and
 * MagZoom's `layouts/full/header.html`, which is the same block on both.
 *
 * TopMags keeps its own: its bag is a different design — a salmon shopping bag with
 * "Shopping Bag" beside it, and a count that shows at zero — and it lives inside that
 * brand's account chips rather than on its own.
 *
 * The legacy header draws a cart PNG out of the site's resources directory and hides the
 * count entirely at zero. This uses the icon set the rest of the V2 tree draws from, and
 * keeps the "nothing to show at zero" behaviour: a badge reading 0 is noise.
 *
 * `cartCount` is `items_quantity` and counts copies, so two of one title reads as 2 — the
 * same number `{cart.totals.qty}` prints on the legacy header, and the one every other
 * brand's badge shows.
 */
export default function CartButton() {
  const { buildUrl } = useUrlGenerator();
  const { cartCount, loaded } = useCart();

  return (
    <Link href={buildUrl('/cart')} className="relative inline-flex text-slate-700 hover:text-primary" ariaLabel="Cart">
      <FontAwesomeIcon icon={faCartShopping} size="lg" />
      {/* Not until the cart has loaded: the count is unknown before then, not zero. */}
      {loaded && cartCount > 0 && (
        <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full bg-primary px-1 text-center text-[11px] font-semibold leading-[18px] text-white">
          {cartCount}
        </span>
      )}
    </Link>
  );
}
